import { createBrowserRouter, createHashRouter, Navigate } from 'react-router';
import { RequireAuth } from './guards';
import { NotFoundPage } from './NotFoundPage';
import { enableScreenTransitions } from './screenTransitions';
import { lz } from './lazyRoute';
import { RouteError } from './RouteError';

export const routes = [
  // Любой сбой раздела — понятный экран, а не «Unexpected Application Error».
  {
    errorElement: <RouteError />,
    children: [
  { path: '/login', lazy: lz(() => import('@/features/auth/LoginPage'), 'LoginPage') },
  // Документация без входа: ссылка с экрана входа.
  { path: '/help', lazy: lz(() => import('@/features/help/manual/ManualPage'), 'PublicManualPage') },
  { path: '/help/:article', lazy: lz(() => import('@/features/help/manual/ManualPage'), 'PublicManualPage') },
  { path: '/password/reset', lazy: lz(() => import('@/features/auth/PasswordResetPages'), 'PasswordResetRequestPage') },
  { path: '/password/reset/:token', lazy: lz(() => import('@/features/auth/PasswordResetPages'), 'PasswordResetConfirmPage') },
  // Подтверждение нового e-mail: ссылка из письма, открыть можно и без входа.
  { path: '/email/confirm/:token', lazy: lz(() => import('@/features/auth/EmailConfirmPage'), 'EmailConfirmPage') },
  { path: '/sso/confirm/:workspaceId/:token', lazy: lz(() => import('@/features/auth/SsoConfirmPage'), 'SsoConfirmPage') },
  // Регистрация по приглашению: ссылка из письма.
  { path: '/join/:token', lazy: lz(() => import('@/features/auth/JoinPage'), 'JoinPage') },
  // Секретный чат по ссылке: гость без аккаунта, ключ шифрования — после «#».
  { path: '/s/:key', lazy: lz(() => import('@/features/secretlink/GuestPage'), 'GuestPage') },
  // Документ по публичной ссылке: читатель без аккаунта, только чтение.
  { path: '/p/:token', lazy: lz(() => import('@/features/publicdoc/PublicDocPage'), 'PublicDocPage') },
  { path: '/book/:slug', lazy: lz(() => import('@/features/booking/BookingPage'), 'BookingPage') },
  // Публичная доска запросов и ChangeLog компании: клиент без входа.
  { path: '/board/:slug', lazy: lz(() => import('@/features/publicBoard/PublicBoardPage'), 'PublicBoardPage') },
  { path: '/m/:code', lazy: lz(() => import('@/features/conference/GuestMeetPage'), 'GuestMeetPage') },
  { path: '/connect', lazy: lz(() => import('@/features/secretlink/ConnectPage'), 'ConnectPage') },
  {
    element: <RequireAuth />,
    children: [
      { path: '/', element: <Navigate to="/w" replace /> },
      { path: '/w', lazy: lz(() => import('@/features/workspace/WorkspacePickerPage'), 'WorkspacePickerPage') },
      { path: '/oauth/authorize', lazy: lz(() => import('@/features/integrations/ConsentPage'), 'ConsentPage') },
      // Приглашение другой компании в общий канал: ссылка из письма, принять — после входа.
      { path: '/connect/:token', lazy: lz(() => import('@/features/connect/AcceptInvitePage'), 'AcceptInvitePage') },
      {
        path: '/w/:workspaceId',
        lazy: lz(() => import('./WorkspaceLayout'), 'WorkspaceLayout'),
        children: [
          // Ошибка страницы внутри раздела не убирает оболочку (боковую панель).
          {
            errorElement: <RouteError />,
            children: [
          { index: true, lazy: lz(() => import('@/features/layout/HomeIndex'), 'HomeIndex') },
          { path: 'home', lazy: lz(() => import('@/features/home/MyDayPage'), 'MyDayPage') },
          { path: 'c/:conversationId', lazy: lz(() => import('@/features/conversation/ConversationPage'), 'ConversationPage') },
          { path: 'dms', lazy: lz(() => import('@/features/inbox/DmsPage'), 'DmsPage') },
          { path: 'threads', lazy: lz(() => import('@/features/inbox/ThreadsPage'), 'ThreadsPage') },
          { path: 'unreads', lazy: lz(() => import('@/features/inbox/UnreadsPage'), 'UnreadsPage') },
          { path: 'drafts', lazy: lz(() => import('@/features/inbox/DraftsPage'), 'DraftsPage') },
          { path: 'activity', lazy: lz(() => import('@/features/inbox/ActivityPage'), 'ActivityPage') },
          { path: 'saved', lazy: lz(() => import('@/features/inbox/SavedPage'), 'SavedPage') },
          { path: 'todos', lazy: lz(() => import('@/features/inbox/TodosPage'), 'TodosPage') },
          { path: 'docs', lazy: lz(() => import('@/features/docs/DocsPage'), 'DocsPage') },
          { path: 'docs/sync', lazy: lz(() => import('@/features/docs/sync/SyncPage'), 'SyncPage') },
          { path: 'calendar', lazy: lz(() => import('@/features/calendar/CalendarPage'), 'CalendarPage') },
          { path: 'screenshots', lazy: lz(() => import('@/features/screenshots/ScreenshotsPage'), 'ScreenshotsPage') },
          { path: 'clients', lazy: lz(() => import('@/features/clients/ClientsPage'), 'ClientsPage') },
          { path: 'clients/:chatId', lazy: lz(() => import('@/features/clients/ClientsPage'), 'ClientsPage') },
          { path: 'meet', lazy: lz(() => import('@/features/conference/MeetingsPage'), 'MeetingsPage') },
          { path: 'meet/:id', lazy: lz(() => import('@/features/conference/MeetingRoomPage'), 'MeetingRoomPage') },
          { path: 'docs/:docId', lazy: lz(() => import('@/features/docs/DocumentPage'), 'DocumentPage') },
          { path: 'assistant', lazy: lz(() => import('@/features/assistant/AssistantPage'), 'AssistantPage') },
          { path: 'assistant/projects/:projectId', lazy: lz(() => import('@/features/assistant/Projects'), 'ProjectPage') },
          { path: 'assistant/:chatId', lazy: lz(() => import('@/features/assistant/AssistantPage'), 'AssistantPage') },
          { path: 'analytics', lazy: lz(() => import('@/features/analytics/AnalyticsPage'), 'AnalyticsPage') },
          { path: 'analytics/:tab', lazy: lz(() => import('@/features/analytics/AnalyticsPage'), 'AnalyticsPage') },
          { path: 'screen-time', lazy: lz(() => import('@/features/screenTime/ScreenTimePage'), 'ScreenTimePage') },
          { path: 'screen-time/:tab', lazy: lz(() => import('@/features/screenTime/ScreenTimePage'), 'ScreenTimePage') },
          { path: 'awards', lazy: lz(() => import('@/features/awards/AwardsPage'), 'AwardsPage') },
          { path: 'awards/:tab', lazy: lz(() => import('@/features/awards/AwardsPage'), 'AwardsPage') },
          { path: 'search', lazy: lz(() => import('@/features/inbox/SearchPage'), 'SearchPage') },
          // Телефон: вкладка «⋯» — всё, что на компьютере в полосе разделов и под «+».
          { path: 'more', lazy: lz(() => import('@/features/layout/MorePage'), 'MorePage') },
          { path: 'sections', lazy: lz(() => import('@/features/layout/SectionsPage'), 'SectionsPage') },
          { path: 'whatsnew', lazy: lz(() => import('@/features/whatsnew/WhatsNewPage'), 'WhatsNewPage') },
          { path: 'onboarding', lazy: lz(() => import('@/features/onboarding/OnboardingPage'), 'OnboardingPage') },
          { path: 'help', lazy: lz(() => import('@/features/help/manual/ManualPage'), 'ManualPage') },
          { path: 'help/:article', lazy: lz(() => import('@/features/help/manual/ManualPage'), 'ManualPage') },
          { path: 'design', lazy: lz(() => import('@/features/design/DesignSystemPage'), 'DesignSystemPage') },
          { path: 'requests', lazy: lz(() => import('@/features/requests/RequestsPage'), 'RequestsPage') },
          { path: 'channels', lazy: lz(() => import('@/features/channels/BrowseChannelsPage'), 'BrowseChannelsPage') },
          { path: 'people', lazy: lz(() => import('@/features/people/PeopleDirectory'), 'PeopleDirectory') },
          { path: 'org', lazy: lz(() => import('@/features/org/OrgPage'), 'OrgPage') },
          { path: 'settings', element: <Navigate to="profile" replace /> },
          { path: 'settings/:tab', lazy: lz(() => import('@/features/settings/SettingsPage'), 'SettingsPage') },
          { path: 'apps', lazy: lz(() => import('@/features/integrations/ConnectionsPage'), 'ConnectionsPage') },
          { path: 'ai', lazy: lz(() => import('@/features/ai-admin/AiAdminPage'), 'AiAdminPage') },
          { path: 'interpreter', lazy: lz(() => import('@/features/interpreter/InterpreterSettingsPage'), 'InterpreterSettingsPage') },
          { path: 'meetings', lazy: lz(() => import('@/features/meetings/MeetingSettingsPage'), 'MeetingSettingsPage') },
          { path: 'apps/:appId', lazy: lz(() => import('@/features/integrations/AppPage'), 'AppPage') },
          { path: 'admin/members', lazy: lz(() => import('@/features/people/PeopleDirectory'), 'PeopleAdminRedirect') },
          { path: 'admin/workspace', lazy: lz(() => import('@/features/admin/AdminWorkspacePage'), 'AdminWorkspacePage') },
          { path: 'admin/history', lazy: lz(() => import('@/features/admin/AdminHistoryPage'), 'AdminHistoryPage') },
          { path: 'admin/directory', element: <Navigate to="../admin/workspace?tab=ldap" replace /> },
          { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
    ],
  },
];

// Демо-версия открывается одним файлом, поэтому адрес страницы хранится после «#».
export const createRouter = () => enableScreenTransitions(import.meta.env.VITE_DEMO === '1' ? createHashRouter(routes) : createBrowserRouter(routes));
